import { Component, Inject, OnInit } from '@angular/core';
import {
  DwActionEventInfo,
  DwActionPreference,
  DwComponent,
  DwComponentType,
  DwDataSort,
  DwEventService,
  DwGridActionLocation,
  DwGridEvent,
  DwGridEventType,
  DwMetaDataActionExpressionService,
  DwMetaDataFilterTileState,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwQueryFilterGroup,
  DwQueryHelper,
  DwRoutingService
} from '@devwareapps/devware-cap';

import {
  AppMetaData,
  AppMetaDataItemNames,
  AppMetaDataLookups,
  QuestionEntity,
  QuestionStatusAllItems,
  QuestionTypeAllItems
} from '../../../../meta-data/app-meta-data.service';
import { AdminManagerService } from '../../../admin/services/admin-manager.service';
import { DwAviatorGridRendererBaseComponent } from '../../../shared/components/grids/dw-aviator-grid-renderer-base.component';

/** Row shape returned by the "Question Query with Tags" grid query (relation fields are item-prefixed). */
interface QuestionBankRow extends QuestionEntity {
  Tags?: string;
  Quizzes?: string;
  CourseCourseName?: string;
  CourseModuleModuleName?: string;
}

interface QuestionBankRowVm {
  row: QuestionBankRow;
  key: number;
  name: string;
  typeCode: string;
  typeTitle: string;
  statusClass: string;
  hasModule: boolean;
  questionText: string;
  contextLine: string;
  usedInLabel: string;
}

type QuestionBankSortKey = 'recent' | 'name' | 'newest';

const SORTS: { [key in QuestionBankSortKey]: { label: string; field: string; ascending: boolean } } = {
  recent: { label: 'Recently updated', field: 'ModifiedDateTime', ascending: false },
  name: { label: 'Name', field: 'QuestionName', ascending: true },
  newest: { label: 'Newest', field: 'QuestionId', ascending: false }
};

const TYPE_CODES: { [id: number]: { code: string; title: string } } = {
  [QuestionTypeAllItems.TrueOrFalse]: { code: 'TF', title: 'True or false' },
  [QuestionTypeAllItems.MultipleChoice]: { code: 'MC', title: 'Multiple choice' },
  [QuestionTypeAllItems.MultipleAnswer]: { code: 'MS', title: 'Multiple answer' }
};

const STATUS_CLASSES: { [id: number]: string } = {
  [QuestionStatusAllItems.Active]: 'qb-dot--active',
  [QuestionStatusAllItems.Draft]: 'qb-dot--draft',
  [QuestionStatusAllItems.Inactive]: 'qb-dot--inactive',
  [QuestionStatusAllItems.Deleted]: 'qb-dot--deleted'
};

/** Stock grid actions that the custom toolbar replaces with its own controls. */
const HIDDEN_TOOLBAR_ACTIONS = /quick.?search|filter.?toggle/i;

@DwComponent({
  key: 'question-bank-grid-renderer',
  name: 'Question Bank Grid Renderer',
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.Question,
  componentType: DwComponentType.gridRenderer
})
@Component({
  selector: 'app-question-bank-grid-renderer',
  templateUrl: './question-bank-grid-renderer.component.html',
  styleUrls: ['./question-bank-grid-renderer.component.scss']
})
export class QuestionBankGridRendererComponent extends DwAviatorGridRendererBaseComponent<QuestionBankRow> implements OnInit {
  coursesLookupId = AppMetaDataLookups.CourseAllItems;
  modulesLookupId = AppMetaDataLookups.CourseModuleAllItems;
  tagsLookupId = AppMetaDataLookups.QuestionTagAllItems;
  quizzesLookupId = AppMetaDataLookups.QuizAllItems;

  searchText = '';
  sortKey: QuestionBankSortKey = 'recent';
  sortOptions = (Object.keys(SORTS) as QuestionBankSortKey[]).map(key => ({ key, label: SORTS[key].label }));

  customFilters = { courseId: null as string, moduleId: null as string, tagId: null as string, quizId: null as string, noModule: false };

  rowVms: QuestionBankRowVm[] = [];
  selectedKeys = new Set<number>();
  allVisibleSelected = false;

  leftActions: DwActionPreference[] = [];
  rightActions: DwActionPreference[] = [];
  bulkActions: DwActionPreference[] = [];
  rowActions: DwActionPreference[] = [];

  emptyTitle = 'No questions found';
  emptyMessage = 'Try adjusting your search or filter criteria.';

  constructor(
    dwRoutingService: DwRoutingService,
    adminManagerService: AdminManagerService,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwEventService: DwEventService,
    private mdActionExpressionService: DwMetaDataActionExpressionService
  ) {
    super(dwRoutingService, adminManagerService, dwMetaDataService, dwEventService);
  }

  ngOnInit(): void {
    this.configureTextSearch([AppMetaData.Question.Attributes.QuestionName, AppMetaData.Question.Attributes.Question], 400);
  }

  setupRenderer(): void {
    this.rebuildActions();
    this.applyCurrentSort();
  }

  protected onGridConfigUpdated(): void {
    this.rebuildActions();
  }

  protected filterTileStateChange(filterTileState: DwMetaDataFilterTileState): void {
    const tile = filterTileState?.filterTileConfig?.tileDisplay;
    this.emptyTitle = tile ? `No ${tile.toLowerCase()} questions` : 'No questions found';
  }

  protected afterLoad(items: QuestionBankRow[]): void {
    this.rowVms = (items || []).map(row => this.buildRowVm(row));
    this.syncSelectionState();
  }

  protected onLoadError(): void {
    this.rowVms = [];
  }

  protected clearRendererState(): void {
    super.clearRendererState();
    this.rowVms = [];
    this.selectedKeys.clear();
    this.allVisibleSelected = false;
  }

  protected resolveInitialPageSize(): number {
    return 50;
  }

  protected getEffectiveSorts(): DwDataSort[] {
    const sort = SORTS[this.sortKey];
    return [{ fieldName: sort.field, ascending: sort.ascending } as DwDataSort];
  }

  protected areItemsEqual(a: QuestionBankRow, b: QuestionBankRow): boolean {
    return a?.QuestionId === b?.QuestionId;
  }

  // ── Search / filter / sort ──────────────────────────────────

  searchTextChanged(): void {
    this.applyTextFilter(this.searchText);
  }

  sortChanged(): void {
    this.applyCurrentSort();
  }

  toggleNoModule(): void {
    this.customFilters.noModule = !this.customFilters.noModule;
    if (this.customFilters.noModule) {
      this.customFilters.moduleId = null;
    }
    this.customFiltersChanged();
  }

  clearCustomFilters(): void {
    this.customFilters = { courseId: null, moduleId: null, tagId: null, quizId: null, noModule: false };
    this.searchText = '';
    this.currentSearchText = '';
    this.customFiltersChanged();
  }

  customFiltersChanged(): void {
    const qh = new DwQueryHelper();
    const filterGroup = new DwQueryFilterGroup();

    this.addFilter(filterGroup, this.customFilters.courseId, AppMetaData.Question.Attributes.CourseId);
    this.addFilter(filterGroup, this.customFilters.moduleId, AppMetaData.Question.Attributes.CourseModuleId);
    // ponytail: relation filters assume the grid query includes the QuestionTagLink / QuizQuestionLink relations
    this.addFilter(filterGroup, this.customFilters.tagId, AppMetaData.QuestionTagLink.Attributes.QuestionTagId);
    this.addFilter(filterGroup, this.customFilters.quizId, AppMetaData.QuizQuestionLink.Attributes.QuizId);

    if (this.customFilters.noModule) {
      filterGroup.AddFilterAnd(qh.filters.IsBlank(AppMetaData.Question.Attributes.CourseModuleId.fieldNameWithItemPrefix));
    }

    // Text search lives in its own OR group so it ANDs with the dropdown filters above
    const searchText = (this.currentSearchText || '').trim();
    if (searchText) {
      const textGroup = new DwQueryFilterGroup();
      this.addTextSearchFilter(textGroup, searchText, this.searchTextAttrs);
      if (/^\d+$/.test(searchText)) {
        textGroup.AddFilterOr(qh.filters.Equal(AppMetaData.Question.Attributes.QuestionId.fieldNameWithItemPrefix, Number(searchText)));
      }
      filterGroup.AddFilterAnd(textGroup);
    }

    this.applyCustomFilters(filterGroup);
  }

  /** Base adds the text search as top-level ORs, which would swallow the AND filters; the group is already complete here. */
  protected applyCustomFilters(filterGroup: DwQueryFilterGroup): void {
    this.hasFilters = filterGroup.Filters.length > 0;
    this.currentFinalFilters = filterGroup;
    this.applyFilters(this.filters);
  }

  // ── Selection ───────────────────────────────────────────────

  toggleRow(vm: QuestionBankRowVm, event?: Event): void {
    event?.stopPropagation();
    this.toggleSelectedItem(vm.row);
    this.syncSelectionState();
  }

  toggleAllVisible(): void {
    this.setSelectedItems(this.allVisibleSelected ? [] : this.items.slice());
    this.syncSelectionState();
  }

  clearSelection(): void {
    super.clearSelection();
    this.syncSelectionState();
  }

  isSelected(vm: QuestionBankRowVm): boolean {
    return this.selectedKeys.has(vm.key);
  }

  // ── Row + action events (handled by the Devware grid host) ──

  onRowClick(vm: QuestionBankRowVm, event?: Event): void {
    this.setSelectedItems([vm.row]);
    this.syncSelectionState();
    this.onGridEvent({ eventType: DwGridEventType.rowSelectionChange, eventData: this.selectedItems, dwBrowserEvent: null } as DwGridEvent);
    this.onGridEvent({ eventType: DwGridEventType.gridRowClick, eventData: vm.row, browserEvent: event, dwBrowserEvent: null } as DwGridEvent);
  }

  onRowDoubleClick(vm: QuestionBankRowVm, event?: Event): void {
    this.onGridEvent({ eventType: DwGridEventType.gridRowDoubleClick, eventData: vm.row, browserEvent: event, dwBrowserEvent: null } as DwGridEvent);
  }

  onRowKeydown(event: KeyboardEvent, vm: QuestionBankRowVm): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.onRowClick(vm, event);
  }

  onRowAction(actionEvent: DwActionEventInfo, vm: QuestionBankRowVm): void {
    if (!actionEvent?.actionPreference) {
      return;
    }
    this.onGridEvent({
      eventType: DwGridEventType.rowActionButton,
      eventData: vm.row,
      actionPreference: actionEvent.actionPreference,
      browserEvent: null,
      dwBrowserEvent: actionEvent.dwBrowserEvent
    } as DwGridEvent);
  }

  onToolbarAction(actionEvent: DwActionEventInfo): void {
    this.fireGridAction(actionEvent, null);
  }

  onBulkAction(actionEvent: DwActionEventInfo): void {
    this.fireGridAction(actionEvent, this.selectedItems);
  }

  onScroll(): void {
    this.nextPage();
  }

  trackByRow(index: number, vm: QuestionBankRowVm): number {
    return vm.key || index;
  }

  get shownLabel(): string {
    const loaded = this.items.length;
    // ponytail: the ORM page result carries no total, so fall back to the selected status tile count
    const tileTotal = (!this.hasFilters && !this.currentSearchText) ? Number(this.selectedFilterTileState?.metricValue) : NaN;
    const total = this.totalCount || (isNaN(tileTotal) ? 0 : tileTotal);
    return total > loaded ? `${loaded.toLocaleString()} of ${total.toLocaleString()}` : `${loaded.toLocaleString()} shown`;
  }

  // ── Internals ───────────────────────────────────────────────

  private fireGridAction(actionEvent: DwActionEventInfo, rows: QuestionBankRow[] | null): void {
    if (!actionEvent?.actionPreference) {
      return;
    }
    this.onGridEvent({
      eventType: DwGridEventType.gridActionButton,
      eventCommand: actionEvent.actionPreference.actionKey,
      actionPreference: actionEvent.actionPreference,
      eventData: rows,
      dwBrowserEvent: actionEvent.dwBrowserEvent
    } as DwGridEvent);
  }

  private rebuildActions(): void {
    const prefs = this.gridConfig?.actionPreferences || {};
    const context = (this.gridConfig as any)?.resolvedContext || null;
    const resolve = (location: string): DwActionPreference[] =>
      this.mdActionExpressionService.executeActionEnabledVisibleRules(prefs[location] || [], null, context).initialResults || [];

    const isCustomReplaced = (a: DwActionPreference) => HIDDEN_TOOLBAR_ACTIONS.test(`${a.actionKey} ${a.actionDisplay}`);

    this.leftActions = resolve(DwGridActionLocation.actionBarLeft).filter(a => !isCustomReplaced(a));
    this.rightActions = resolve(DwGridActionLocation.actionBarRight).filter(a => !isCustomReplaced(a));
    this.bulkActions = resolve(DwGridActionLocation.rowContextMenu);
    this.rowActions = resolve(DwGridActionLocation.rowButton);
  }

  private applyCurrentSort(): void {
    this.setSorts(this.getEffectiveSorts());
  }

  private syncSelectionState(): void {
    this.selectedKeys = new Set((this.selectedItems || []).map(item => item.QuestionId));
    this.allVisibleSelected = this.items.length > 0 && this.items.every(item => this.selectedKeys.has(item.QuestionId));
  }

  private buildRowVm(row: QuestionBankRow): QuestionBankRowVm {
    const type = TYPE_CODES[row.QuestionTypeId] || { code: '', title: '' };
    const moduleName = row.CourseModuleModuleName || row.CourseModuleIdDisplay || '';
    const courseName = row.CourseCourseName || row.CourseIdDisplay || '';
    const quizzes = (row.Quizzes || '').split(',').map(q => q.trim()).filter(q => !!q);

    const contextParts = [moduleName || 'No module', courseName].filter(p => !!p);
    const contextLine = quizzes.length ? `${contextParts.join(' · ')} — ${quizzes.join(', ')}` : contextParts.join(' · ');

    return {
      row,
      key: row.QuestionId,
      name: row.QuestionName || `Question #${row.QuestionId}`,
      typeCode: type.code,
      typeTitle: type.title,
      statusClass: STATUS_CLASSES[row.QuestionStatusId] || 'qb-dot--inactive',
      hasModule: !!row.CourseModuleId,
      questionText: stripHtml(row.Question),
      contextLine,
      usedInLabel: quizzes.length === 0 ? '—' : quizzes.length === 1 ? '1 quiz' : `${quizzes.length} quizzes`
    };
  }
}

function stripHtml(value: string): string {
  return (value || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
}
